import api from '../api/instance';
 
import { Page } from '../models/page';
import { UserProjectMapping } from '../models/user-project-mapping.model';
import { PrivateURLConstants } from '@constants/private-url.constant';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { USER_PROJECT_MAPPING_KEY } from '@modules/project-switcher/constants';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';

const nameSpace = PrivateURLConstants.USER_PROJECT_MAPPING;

const findAll = (filterQuery?: string) => {
  filterQuery = filterQuery ? filterQuery : '';
  return api
    .get(nameSpace + filterQuery)
    .then((res) =>
      new Page<UserProjectMapping>().deserialize(res, UserProjectMapping),
    )
    .catch((e) => Promise.reject(e));
};

const create = (payload: UserProjectMapping) => {
  return api
    .post(nameSpace, payload)
    .then((res) => new UserProjectMapping().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const remove = (id: number) => {
  return api.remove(`${nameSpace}/${id}`);
};

const update = (id: number, payload: UserProjectMapping) => {
  return api
    .put(`${nameSpace}/${id}`, payload)
    .then((res) => new UserProjectMapping().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const UserProjectMappingService = {
  findAll,
  create,
  remove,
  update,
};

export default UserProjectMappingService;

const useRefetchUserProjectMappingCache = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.refetchQueries(USER_PROJECT_MAPPING_KEY);
  };
};

export const useFetchUserProjectMapping = ({
  filterQuery = '',
  enabled = true,
  onError = console.error,
  onSuccess = (res: Page<UserProjectMapping>) => {}
}) => {
  const { getCachingTime } = useReactQueryCaching(
    USER_PROJECT_MAPPING_KEY,
    false,
  );

  return useQuery({
    queryKey: [USER_PROJECT_MAPPING_KEY, filterQuery],
    queryFn: () => findAll(filterQuery),
    // staleTime: getCachingTime(),
    // cacheTime: getCachingTime(),
    enabled,
    onError,
    onSuccess: (res) => onSuccess(res),
  });
};

export const useCreateUserProjectMappingMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchUserProjectMappingCache = useRefetchUserProjectMappingCache();

  return useMutation(
    create,
    {
      onSuccess: (data) => {
        onSuccess(data);
        refetchUserProjectMappingCache();
      },
      onError,
      onSettled
    }
  );
};

export const useUpdateUserProjectMappingMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {}
}) => {
  const refetchUserProjectMappingCache = useRefetchUserProjectMappingCache();

  return useMutation(
    ({memberId, payload}) => {
      return update(memberId, payload)
    },
    {
      onSuccess: (data) => {
        onSuccess(data);
        refetchUserProjectMappingCache();
      },
      onError,
      onSettled
    }
  );
};

export const useDeleteUserProjectMappingMutation = ({
  onSuccess = () => {},
  onError = console.error,
  onSettled = () => {}
}) => {
  const refetchUserProjectMappingCache = useRefetchUserProjectMappingCache();

  return useMutation(
    remove,
    {
      onSuccess: () => {
        onSuccess();
        refetchUserProjectMappingCache();
      },
      onError,
      onSettled
    }
  );
};
